// src/hooks/useCustomBlocks.js
import { useState, useEffect } from "react";
import axios from "axios";
import { useGroupe } from "../context/GroupeContext.js";
import { getMomentColor } from "../constants/moments.js";
import { logDev } from "../utils/logger.js";

export const useCustomBlocks = () => {
  const [customBlocks, setCustomBlocks] = useState({});
  const [newBlockName, setNewBlockName] = useState("");
  const { groupeId } = useGroupe();

  // 1️⃣ Charger les blocs depuis la base
  useEffect(() => {
    const fetchBlocks = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/customblocks/${groupeId}`);
        const blocks = res.data || [];
        const formatted = {};
        blocks.forEach((b) => {
          formatted[b.name] = b.items;
        });
        logDev("[useCustomBlocks] blocs chargés depuis API", { groupeId, formatted });
        setCustomBlocks(formatted);
      } catch (err) {
        logDev("❌ Erreur chargement blocs personnalisés", { message: err.message, stack: err.stack });

      }
    };
    if (groupeId) fetchBlocks();
  }, [groupeId]);

  // 2️⃣ Ajouter un bloc
  const addCustomBlock = async (name) => {
    if (!name || customBlocks[name] || !groupeId) {
      console.warn("Bloc non créé : nom ou groupeId manquant");
      return;
    }
    try {
      const payload = { groupe: groupeId, name, items: [] };
      logDev("[useCustomBlocks] tentative ajout bloc", { groupeId, name });
      await axios.post(`${process.env.REACT_APP_API}/customblocks`, payload);
      logDev("[useCustomBlocks] bloc ajouté", { name });
      setCustomBlocks((prev) => ({
        ...prev,
        [name]: [{ name: "", quantity: "" }]
      }));
      setNewBlockName("");
    } catch (err) {
      logDev("❌ Erreur ajout bloc", {
        message: err.message,
        response: err.response?.data,
        stack: err.stack
      });
    }
  };

  // 3️⃣ Modifier un item
  const handleCustomItemChange = async (blockName, index, field, value) => {
    const updatedItems = [...customBlocks[blockName]];
    updatedItems[index][field] = value;
    logDev("[useCustomBlocks] modification item", { blockName, index, field, value });
    setCustomBlocks((prev) => ({ ...prev, [blockName]: updatedItems }));
    try {
      await axios.put(`${process.env.REACT_APP_API}/customblocks/update`, {
        groupe: groupeId,
        name: blockName,
        items: updatedItems
      });
    } catch (err) {
      logDev("❌ Erreur mise à jour item", { message: err.message, stack: err.stack });
    }
  };

  // 4️⃣ Renommer un bloc (API + local)
  const renameCustomBlock = async (oldName, newName) => {
    if (!groupeId || !oldName || !newName) {
      console.warn("Renommage impossible : champs manquants");
      return;
    }
    try {
      await axios.put(`${process.env.REACT_APP_API}/customblocks/rename`, {
        groupe: groupeId,
        oldName,
        newName
      });
      logDev("[useCustomBlocks] renommage bloc", { groupeId, oldName, newName });
      setCustomBlocks((prev) => {
        const updated = { ...prev };
        updated[newName] = updated[oldName];
        delete updated[oldName];
        return updated;
      });
    } catch (err) {
      logDev("❌ Erreur renommage bloc", {
      message: err.message,
      response: err.response?.data,
      stack: err.stack
    });
    }
  };

  // 5️⃣ Ajouter un item
  const addCustomItem = async (blockName) => {
    const updatedItems = [...customBlocks[blockName], { name: "", quantity: "" }];
    logDev("[useCustomBlocks] ajout item", { blockName, updatedItems });
    setCustomBlocks((prev) => ({ ...prev, [blockName]: updatedItems }));
    try {
      await axios.put(`${process.env.REACT_APP_API}/customblocks/update`, {
        groupe: groupeId,
        name: blockName,
        items: updatedItems
      });
    } catch (err) {
      logDev("❌ Erreur ajout item", { message: err.message, stack: err.stack });
    }
  };

  // 6️⃣ Sauvegarder un item
  const saveCustomItem = async (blockName, index, item) => {
    const updatedItems = [...customBlocks[blockName]];
    updatedItems[index] = { ...item, saved: true };
    logDev("[useCustomBlocks] sauvegarde item", { blockName, index, item });
    setCustomBlocks((prev) => ({ ...prev, [blockName]: updatedItems }));
    try {
      await axios.put(`${process.env.REACT_APP_API}/customblocks/update`, {
        groupe: groupeId,
        name: blockName,
        items: updatedItems
      });
    } catch (err) {
      logDev("❌ Erreur sauvegarde item", { message: err.message, stack: err.stack });
    }
  };

  // 7️⃣ Supprimer un item
  const removeCustomItem = async (blockName, index) => {
    const updatedItems = customBlocks[blockName].filter((_, i) => i !== index);
    logDev("[useCustomBlocks] suppression item", { blockName, index });
    setCustomBlocks((prev) => ({ ...prev, [blockName]: updatedItems }));
    try {
      await axios.put(`${process.env.REACT_APP_API}/customblocks/update`, {
        groupe: groupeId,
        name: blockName,
        items: updatedItems
      });
    } catch (err) {
      logDev("❌ Erreur suppression item", { message: err.message, stack: err.stack });
    }
  };

  // 8️⃣ Récupérer tous les items
  const getAllCustomItems = () => {
  const items = [];
  Object.entries(customBlocks).forEach(([blockName, bloc]) => {
    bloc.forEach(({ _id, name, quantity, color }) => {
      const trimmed = name?.trim();
      if (trimmed) {
        items.push({
          articleId: _id,  
          _id,
          name: trimmed,
          quantity: quantity || 0,
          theme: blockName,                 // 👈 le nom du bloc devient le thème
          moment: "Custom",                 // 👈 toujours "Custom"
          source: "custom",                 // 👈 pour différencier
          categorie: "Divers", 
          color: color || getMomentColor("Custom")
        });
      }
    });
  });
  logDev("[useCustomBlocks] récupération de tous les items", { total: items.length, items });
  return items;
};

// 9️⃣ Supprimer un bloc entier
const removeCustomBlock = async (blockName) => {
  if (!groupeId || !blockName) return;
  try {
    await axios.delete(`${process.env.REACT_APP_API}/customblocks/${groupeId}/${encodeURIComponent(blockName)}`);
    logDev("[useCustomBlocks] bloc supprimé", { groupeId, blockName });
    setCustomBlocks((prev) => {
      const updated = { ...prev };
      delete updated[blockName];
      return updated;
    });
  } catch (err) {
    logDev("❌ Erreur suppression bloc", {
      message: err.message,
      response: err.response?.data,
      stack: err.stack
    });
  }
};

  return {
    customBlocks,
    newBlockName,
    setNewBlockName,
    addCustomBlock,
    handleCustomItemChange,
    addCustomItem,
    removeCustomItem,
    renameCustomBlock,
    removeCustomBlock,
    saveCustomItem,
    getAllCustomItems
  };
};
